import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiGatewayService } from '~app/shared/api-gateway.service';

/** Valid Flux window functions */
const windowFunctions = [
  // aggregate functions
  'count', 'integral', 'mean', 'median', 'mode', 'skew', 'spread', 'stddev', 'sum',
  // selector functions
  'distinct', 'last', 'max', 'min',
] as const;
type InfluxWindowFunction = typeof windowFunctions[number];
export function isWindowFunction(name: string): name is InfluxWindowFunction {
  return (windowFunctions as readonly string[]).includes(name);
}

export interface InfluxFilters {
  _start?: string;
  _stop?: string;
  _window?: string;
  _windowFunction?: InfluxWindowFunction;
  [key: string]: string;
}

@Injectable()
export class InfluxService extends ApiGatewayService {
  constructor(protected http: HttpClient) {
    super(http, 'influx');
  }

  public getMeasurements(
    bucket: string,
    filters: InfluxFilters = {},
  ): Promise<string[]> {
    return this.get(['buckets', bucket, 'measurements'], filters);
  }

  public getDistinctValues(
    bucket: string,
    measurement: string,
    field: string,
    filters: InfluxFilters = {},
  ): Promise<string[]> {
    return this.get(['buckets', bucket, 'measurements', measurement, 'distinct', field], filters);
  }

  public getDistinctValuesWithLabel(
    bucket: string,
    measurement: string,
    field: string,
    labelField: string,
    filters: InfluxFilters = {},
  ): Promise<[string, string][]> {
    return this.get(['buckets', bucket, 'measurements', measurement, 'distinct', field, labelField], filters);
  }

  public getTimeline(
    bucket: string,
    measurement: string,
    filters: InfluxFilters = {},
  ): Promise<[string, number | string | boolean][]> {
    return this.get(['buckets', bucket, 'measurements', measurement], filters);
  }

  public measurementExists(
    bucket: string,
    measurement: string,
    filters: InfluxFilters = {},
  ): Promise<boolean> {
    return this.get(['buckets', bucket, 'measurements', measurement, 'exists'], filters);
  }
}
